import { z } from "zod";
import { RiskClass } from "./api-access.js";
import { CostChange, CostPreview } from "./costs.js";

/*
 * Approvals (docs/approvals.md): a call of an API key or an MCP agent that
 * its policy sends for approval (`require_approval`) waits as a request
 * until a person who may do the same approves it with a second factor, or
 * rejects it with a reason. The approved request runs once, exactly as it
 * was asked (its fingerprint), as the key; the caller polls for the result.
 */

export const APPROVAL_STATUSES = [
  /** Waiting for a person. */
  "pending",
  /** Approved; running now. */
  "approved",
  /** Approved and run: `result` holds the answer (2xx). */
  "executed",
  /** Approved, but the call did not succeed: `result` holds the refusal or error. */
  "failed",
  "rejected",
  /** Nobody decided before `expiresAt`. */
  "expired",
] as const;
export const ApprovalStatus = z.enum(APPROVAL_STATUSES);
export type ApprovalStatus = z.infer<typeof ApprovalStatus>;

/** Whether a request is still open (waiting or running). */
export const isOpenApproval = (status: ApprovalStatus): boolean =>
  status === "pending" || status === "approved";

/** Who stands behind the call: the key or connected app, its owner, the person who answers for it, the program. */
export const ApprovalRequester = z.object({
  /** An API key, or a connected app (an OAuth grant, docs/oauth.md). */
  kind: z.enum(["api_key", "oauth_client"]),
  keyId: z.uuid().nullable(),
  /** The connected app, for `oauth_client`. */
  appId: z.uuid().nullable(),
  /** The key's name, or the connected app's. */
  keyName: z.string(),
  /** A person's key, or a service account's. */
  ownerKind: z.enum(["user", "service_account"]),
  ownerName: z.string().nullable(),
  /** The person who answers for the key (the owner, or the service account's owner). */
  userId: z.uuid().nullable(),
  userName: z.string().nullable(),
  /** User-Agent of the calling program. */
  client: z.string().nullable(),
  ip: z.string().nullable(),
  /** `mcp` when an MCP tool made the call, with the tool's name. */
  via: z.enum(["rest", "mcp"]),
  tool: z.string().nullable(),
});
export type ApprovalRequester = z.infer<typeof ApprovalRequester>;

/** The exact request that runs once approved. */
export const ApprovalRequest = z.object({
  method: z.enum(["POST", "PUT", "PATCH", "DELETE"]),
  /** Below `/api/v1`, with the query. */
  path: z.string(),
  /** The route it matched, e.g. `/projects/:projectId/networks/:networkId`. */
  route: z.string(),
  /** The JSON body as sent; null without one. */
  body: z.unknown(),
  /** SHA-256 of method, path and canonical body; approving names it, so nothing else can run. */
  fingerprint: z.string().regex(/^[0-9a-f]{64}$/),
});
export type ApprovalRequest = z.infer<typeof ApprovalRequest>;

/** What the request acts on, from our copy of the project's resources. */
export const ApprovalTarget = z.object({
  id: z.string(),
  kind: z.string().nullable(),
  name: z.string().nullable(),
});
export type ApprovalTarget = z.infer<typeof ApprovalTarget>;

/** How it ended, once run: the route's status and its answer (passwords never kept). */
export const ApprovalResult = z.object({
  status: z.number().int(),
  body: z.unknown(),
});
export type ApprovalResult = z.infer<typeof ApprovalResult>;

export const Approval = z.object({
  id: z.uuid(),
  status: ApprovalStatus,
  action: z.string(),
  risk: RiskClass,
  projectId: z.uuid().nullable(),
  projectName: z.string().nullable(),
  target: ApprovalTarget.nullable(),
  request: ApprovalRequest,
  requester: ApprovalRequester,
  /** Why it waits: the policy statement's reason (`approval_required` by default) and its words. */
  reason: z.string(),
  detail: z.string(),
  /** The policy that asked for approval (`template:full_access`, a library policy, `custom`). */
  policy: z.string().nullable(),
  /** The change the budget guard prices, for calls that add cost; null for the others. */
  costChange: CostChange.nullable(),
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  decidedBy: z.object({ id: z.uuid().nullable(), name: z.string().nullable() }).nullable(),
  decidedAt: z.iso.datetime().nullable(),
  /** The approver's comment, or the reason of a rejection. */
  note: z.string().nullable(),
  executedAt: z.iso.datetime().nullable(),
  result: ApprovalResult.nullable(),
  /** The caller may decide it now (a person who may do the same, while it waits). */
  canDecide: z.boolean(),
  /** Why not, when it waits and the caller may not (`approval_not_allowed`…). */
  cannotDecide: z.string().nullable(),
  /** Where a person decides it. */
  panelUrl: z.url(),
});
export type Approval = z.infer<typeof Approval>;

/**
 * `GET /approvals/:id` for a person: the request with what it would cost now
 * and the budget rule it falls under, for the key's owner (null when it adds no cost).
 */
export const ApprovalDetail = Approval.extend({ cost: CostPreview.nullable() });
export type ApprovalDetail = z.infer<typeof ApprovalDetail>;

export const ApprovalListQuery = z.object({
  /** `open`: waiting or running; `closed`: decided, run or expired; default every one. */
  status: z.enum(["open", "closed"]).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ApprovalListQuery = z.input<typeof ApprovalListQuery>;

export const ApprovalList = z.object({
  items: z.array(Approval),
  /** Open requests the caller may decide (the tab's counter). */
  waitingForMe: z.number().int().nonnegative(),
});
export type ApprovalList = z.infer<typeof ApprovalList>;

/** `POST /approvals/:id/approve`: the fingerprint the approver saw; a changed request is refused. */
export const ApproveRequest = z.object({
  fingerprint: z.string().regex(/^[0-9a-f]{64}$/),
  note: z.string().trim().max(500).optional(),
});
export type ApproveRequest = z.input<typeof ApproveRequest>;

/** `POST /approvals/:id/reject`: a reason the caller is told. */
export const RejectRequest = z.object({ reason: z.string().trim().min(1).max(500) });
export type RejectRequest = z.input<typeof RejectRequest>;

/**
 * The answer `202` of a call that waits for approval (instead of the route's
 * own answer): poll `GET /api/v1/approvals/:id` (`links.approval`) until it
 * leaves `pending`/`approved`; MCP agents call `wait_for_approval`.
 */
export const PendingApproval = z.object({
  status: z.literal("pending_approval"),
  reason: z.string(),
  detail: z.string(),
  approval: Approval,
  links: z.object({ approval: z.string(), panel: z.url() }),
});
export type PendingApproval = z.infer<typeof PendingApproval>;
